/**
 * Cache encryption utilities
 * Handles AES-GCM encryption/decryption for cache entries
 */

import { systemLogger } from '@/lib/logging';

export interface EncryptionResult {
  data: string;
  iv: string;
  tag: string;
}

export class CacheEncryption {
  private encryptionKey: CryptoKey | null = null;
  private readonly algorithm = 'AES-GCM';

  constructor() {
    this.initializeEncryption();
  }

  /**
   * Initialize encryption key
   */
  private async initializeEncryption(): Promise<void> {
    try {
      // Generate a new AES-GCM key
      this.encryptionKey = await crypto.subtle.generateKey(
        {
          name: this.algorithm,
          length: 256,
        },
        false, // not extractable
        ['encrypt', 'decrypt']
      );
    } catch (error) {
      systemLogger.error('Failed to initialize cache encryption:', error);
    }
  }

  /**
   * Encrypt data using AES-GCM
   */
  async encrypt(data: string): Promise<EncryptionResult> {
    if (!this.encryptionKey) {
      await this.initializeEncryption();
      if (!this.encryptionKey) {
        throw new Error('Encryption key not available');
      }
    }

    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);

      // Generate random IV (12 bytes for AES-GCM)
      const iv = crypto.getRandomValues(new Uint8Array(12));

      const encrypted = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv: iv,
        },
        this.encryptionKey,
        dataBuffer
      );

      // AES-GCM returns the ciphertext with the authentication tag appended
      const encryptedArray = new Uint8Array(encrypted);
      const ciphertext = encryptedArray.slice(0, -16); // All but last 16 bytes
      const tag = encryptedArray.slice(-16); // Last 16 bytes

      return {
        data: Array.from(ciphertext).map(b => b.toString(16).padStart(2, '0')).join(''),
        iv: Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(''),
        tag: Array.from(tag).map(b => b.toString(16).padStart(2, '0')).join(''),
      };
    } catch (error) {
      systemLogger.error('Cache encryption failed:', error);
      throw new Error('Failed to encrypt cache data');
    }
  }

  /**
   * Decrypt data using AES-GCM
   */
  async decrypt(encrypted: EncryptionResult): Promise<string> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not available for decryption');
    }

    try {
      // Convert hex strings back to Uint8Arrays
      const ciphertext = new Uint8Array(
        encrypted.data.match(/.{2}/g)!.map(byte => parseInt(byte, 16))
      );
      const iv = new Uint8Array(
        encrypted.iv.match(/.{2}/g)!.map(byte => parseInt(byte, 16))
      );
      const tag = new Uint8Array(
        encrypted.tag.match(/.{2}/g)!.map(byte => parseInt(byte, 16))
      );

      // Combine ciphertext and tag for AES-GCM
      const combined = new Uint8Array(ciphertext.length + tag.length);
      combined.set(ciphertext);
      combined.set(tag, ciphertext.length);

      const decrypted = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: iv,
        },
        this.encryptionKey,
        combined
      );

      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      systemLogger.error('Cache decryption failed:', error);
      throw new Error('Failed to decrypt cache data');
    }
  }

  /**
   * Check if encryption is available
   */
  isEncryptionReady(): boolean {
    return this.encryptionKey !== null;
  }
}